import React from 'react';
import classNames from 'classnames';
import s from './AiAnalyzingBorder.scss';

const RADIUS = 16;
const STROKE = 1.5;
const BLEED = 1;
const INSET = STROKE / 2 + BLEED;
const SEGMENT = 20;
const CARD_PHASE_OFFSETS = [0, 0.85, 1.55, 2.2, 2.95, 3.6];

const COMET_STOPS = [
  { offset: '0%', color: '#caebff' },
  { offset: '45%', color: '#7b91ff' },
  { offset: '100%', color: '#5532da' },
];

const ORBIT_DURATION_S = 2;
const ORBIT_CARD_LEAD_FRACTION_OF_LOOP = 0.08;

export type AiAnalyzingBorderMode = 'travel' | 'grow' | 'fade' | 'off';
export type AiAnalyzingBorderVariant = 'comet' | 'orbit';

interface AiAnalyzingBorderProps {
  mode: AiAnalyzingBorderMode;
  cardIndex: number;
  variant?: AiAnalyzingBorderVariant;
  cardCount?: number;
}

const getOrbitDelay = (cardIndex: number, cardCount: number) =>
  -Math.max(0, cardCount - 1 - cardIndex) *
  ORBIT_DURATION_S *
  ORBIT_CARD_LEAD_FRACTION_OF_LOOP;

export const AiAnalyzingBorder = ({
  mode,
  cardIndex,
  variant = 'comet',
  cardCount = 1,
}: AiAnalyzingBorderProps) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const travelRef = React.useRef<SVGRectElement>(null);
  const offsetRef = React.useRef('0');
  const [size, setSize] = React.useState({ width: 0, height: 0 });
  const isOrbit = variant === 'orbit';
  const gradientId = React.useMemo(
    () => `ai-analyzing-comet-${cardIndex}`,
    [cardIndex],
  );

  React.useLayoutEffect(() => {
    const el = rootRef.current;
    if (isOrbit || !el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) {
        setSize({
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isOrbit]);

  React.useEffect(() => {
    if (
      isOrbit ||
      mode !== 'travel' ||
      typeof requestAnimationFrame === 'undefined'
    ) {
      return undefined;
    }
    let raf = 0;
    const tick = () => {
      const rect = travelRef.current;
      if (rect) {
        offsetRef.current = getComputedStyle(rect).strokeDashoffset;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mode, isOrbit, size.width, size.height]);

  React.useLayoutEffect(() => {
    const rect = travelRef.current;
    if (!rect) {
      return;
    }
    rect.style.strokeDashoffset = mode === 'grow' ? offsetRef.current : '';
  }, [mode, size.width, size.height]);

  React.useLayoutEffect(() => {
    const el = rootRef.current;
    if (!isOrbit || !el) {
      return;
    }
    el.style.setProperty('--orbit-duration', `${ORBIT_DURATION_S}s`);
    el.style.setProperty(
      '--orbit-delay',
      `${getOrbitDelay(cardIndex, cardCount)}s`,
    );
  }, [isOrbit, cardIndex, cardCount]);

  const { width, height } = size;
  const cometDelay = -CARD_PHASE_OFFSETS[cardIndex % CARD_PHASE_OFFSETS.length];

  return (
    <div
      ref={rootRef}
      className={classNames(s.root, {
        [s.orbit]: isOrbit,
        [s.modeTravel]: mode === 'travel',
        [s.modeGrow]: mode === 'grow',
        [s.modeFade]: mode === 'fade',
      })}
      data-hook="ai-analyzing-border"
      aria-hidden
    >
      {isOrbit ? (
        <>
          <div className={s.orbitRing} />
          <div className={s.orbitFill} data-hook="ai-analyzing-orbit-fill" />
        </>
      ) : (
        width > 0 &&
        height > 0 && (
          <svg
            className={s.svg}
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="none"
            fill="none"
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
                {COMET_STOPS.map((stop) => (
                  <stop
                    key={stop.offset}
                    offset={stop.offset}
                    stopColor={stop.color}
                  />
                ))}
              </linearGradient>
            </defs>
            <rect
              ref={travelRef}
              className={s.travel}
              x={INSET}
              y={INSET}
              width={width - INSET * 2}
              height={height - INSET * 2}
              rx={RADIUS}
              pathLength={100}
              stroke={`url(#${gradientId})`}
              strokeDasharray={`${SEGMENT} ${100 - SEGMENT}`}
              style={{ animationDelay: `${cometDelay}s` }}
            />
          </svg>
        )
      )}
    </div>
  );
};
